import { useState, useEffect, useCallback } from 'react';
import { capacidadesAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Boxes, Plus, Link2, Unlink, Loader2, AlertCircle, CheckCircle2, ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import QueSeConfiguraAca from '@/components/QueSeConfiguraAca';

/**
 * Qué sabe hacer esta cápsula, y con qué lo cumple.
 *
 * **Vive al lado de conexiones a propósito.** Un vínculo nombra una conexión, y separar las dos
 * pantallas obligaría a ir y venir para entender una sola cosa.
 *
 * **La distinción que ordena todo:** una conexión es *sistema + credencial*; el vínculo dice
 * *cuál recurso* — cuál proyecto, cuál repositorio. Con el recurso adentro de la conexión, dos
 * proyectos del mismo GitLab necesitan dos conexiones, o sea dos copias del mismo token.
 */
export default function Capacidades({ tenantId, capsuleId, conexiones }) {
  const [filas, setFilas] = useState([]);
  const [herramientas, setHerramientas] = useState([]);
  const [huecos, setHuecos] = useState([]);
  const [abierta, setAbierta] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [nueva, setNueva] = useState(null);

  const cargar = useCallback(async () => {
    if (!tenantId) return;
    setCargando(true);
    try {
      const { data } = await capacidadesAPI.list(tenantId, capsuleId || '');
      setFilas(data.capacidades || []);
      setHerramientas(data.herramientas || []);
      setHuecos(data.huecos || []);
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudieron cargar las capacidades');
    } finally {
      setCargando(false);
    }
  }, [tenantId, capsuleId]);

  useEffect(() => { cargar(); }, [cargar]);

  const declarar = async () => {
    try {
      const { data } = await capacidadesAPI.declarar({ ...nueva, tenant_id: tenantId });
      if (!data.ok) { toast.error(data.errores.join(' · ')); return; }
      toast.success(`Capacidad "${nueva.capacidad_id}" declarada`);
      setNueva(null);
      cargar();
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo declarar');
    }
  };

  return (
    <div className="space-y-3" data-testid="capacidades">
      <QueSeConfiguraAca
        testId="ayuda-capacidades"
        queEs={<>Una <b>capacidad</b> es algo que hace falta poder hacer —«crear una incidencia», «buscar en el código»— <b>sin decir con qué sistema</b>. Un <b>vínculo</b> es la respuesta de esta cápsula: con esta conexión, esta herramienta, sobre este recurso.</>}
        paraQue="El agente pide el verbo y no sabe quién está del otro lado. Por eso la misma habilidad sirve en una empresa que usa GitLab y en otra que usa GitHub, sin reescribir una palabra de su prosa — que es lo más caro de construir."
        aQueAfecta={<>Si una capacidad <b>no está atada</b>, el agente que la pida recibe «esto no está configurado en tu área» y lo dice en vez de prometerlo. Si está atada, la llamada sale contra el sistema que elegiste acá.</>}
        ejemplo={<>«Crear una incidencia» atada a la conexión GitLab, herramienta <code>gitlab_create_issue</code>, recurso <code>PROJECT_ID: 9</code>. <b>El recurso vive en el vínculo y nunca en la conexión</b>: así dos proyectos del mismo GitLab comparten una sola credencial en vez de necesitar dos copias del mismo token.</>}
        ojo={<>Dos avisos que conviene mirar: <b>«sin atar»</b> es un verbo que alguien pide y nadie cumple; <b>«atada y nadie la pide»</b> es configuración que parece viva y ningún agente va a usar, porque ninguna habilidad declara ese verbo.</>}
      />

      <div className="flex items-start justify-between gap-3">
        <p className="text-[10px] text-zinc-500 max-w-2xl">
          Qué sabe hacer esta cápsula, y con qué lo cumple.
        </p>
        <Button size="sm" variant="outline" data-testid="declarar-capacidad"
                className="h-7 text-[10px] rounded-sm gap-1 shrink-0"
                onClick={() => setNueva({ capacidad_id: '', descripcion: '', devuelve: '' })}>
          <Plus className="w-3 h-3" /> Declarar una
        </Button>
      </div>

      {nueva && (
        <Card className="rounded-sm border-brand-border" data-testid="nueva-capacidad">
          <CardContent className="p-3 space-y-2">
            <Fila label="Identificador">
              <Input data-testid="nueva-id" value={nueva.capacidad_id}
                     onChange={(e) => setNueva({ ...nueva, capacidad_id: e.target.value })}
                     className="rounded-sm text-xs h-7 font-mono" placeholder="consultar_cliente" />
            </Fila>
            <Fila label="Qué hace">
              <Input data-testid="nueva-descripcion" value={nueva.descripcion}
                     onChange={(e) => setNueva({ ...nueva, descripcion: e.target.value })}
                     className="rounded-sm text-xs h-7"
                     placeholder="Es lo unico que el modelo lee para decidir cuando usarla" />
            </Fila>
            <Fila label="Qué devuelve">
              <Input data-testid="nueva-devuelve" value={nueva.devuelve}
                     onChange={(e) => setNueva({ ...nueva, devuelve: e.target.value })}
                     className="rounded-sm text-xs h-7" placeholder="La ficha del cliente" />
            </Fila>
            <div className="flex gap-2">
              <Button size="sm" data-testid="guardar-capacidad"
                      className="bg-brand-primary text-white rounded-sm text-xs h-7 px-3"
                      onClick={declarar}>Declarar</Button>
              <Button size="sm" variant="ghost" className="h-7 text-[10px]"
                      onClick={() => setNueva(null)}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <LoQueFaltaAtar
        huecos={huecos} tenantId={tenantId} capsuleId={capsuleId} onCambio={cargar}
      />

      {cargando && <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />}

      {filas.map((c) => (
        <FichaDeCapacidad
          key={c.capacidad_id} capacidad={c} abierta={abierta === c.capacidad_id}
          onAbrir={() => setAbierta(abierta === c.capacidad_id ? null : c.capacidad_id)}
          tenantId={tenantId} capsuleId={capsuleId}
          herramientas={herramientas} conexiones={conexiones} onCambio={cargar}
        />
      ))}
    </div>
  );
}

/**
 * Lo que alguna habilidad pide y esta cápsula no ató.
 *
 * **Hasta hoy este hueco sólo lo veía el modelo.** `sin_atar` existe desde la fase 4 y al agente
 * se le dice que esa capacidad no está configurada —para que diga «todavía no está configurado en
 * mi área» en vez de prometer—, pero la pantalla nunca lo mostró. De los tres lados donde el
 * hueco tenía que verse, faltaba justo **el único que puede arreglarlo**.
 *
 * **Se ofrece cómo cerrarlo, no sólo que existe.** El conector del producto ya sabe con qué
 * herramienta se cumple cada verbo y cómo se llama cada parámetro del otro lado; sin eso, quien
 * lee este aviso tiene que adivinar el mapeo — y adivinarlo mal no falla al atar, falla después.
 */
function LoQueFaltaAtar({ huecos, tenantId, capsuleId, onCambio }) {
  const [atando, setAtando] = useState('');
  if (!huecos.length) return null;

  const atar = async (hueco, receta) => {
    setAtando(hueco.capacidad_id);
    try {
      const { data } = await capacidadesAPI.atar(hueco.capacidad_id, {
        tenant_id: tenantId,
        capsule_id: capsuleId,
        conexion_id: receta.conexion_id,
        herramienta: receta.herramienta,
        mapeo: receta.mapeo,
        recurso: {},
      });
      if (!data.ok) { toast.error(data.errores.join(' · ')); return; }
      toast.success('Atada');
      onCambio();
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo atar');
    } finally {
      setAtando('');
    }
  };

  return (
    <Card className="rounded-sm border-amber-300 bg-amber-50/40" data-testid="lo-que-falta-atar">
      <CardHeader className="py-2 px-3 border-b border-amber-200">
        <CardTitle className="text-xs font-semibold flex items-center gap-1.5 text-amber-900">
          <AlertCircle className="w-3.5 h-3.5" />
          {huecos.length} {huecos.length === 1 ? 'verbo que se pide' : 'verbos que se piden'} y esta cápsula no ató
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 space-y-2">
        <p className="text-[10px] text-amber-900/80">
          El agente lo sabe y avisa que esa acción todavía no está configurada — no la promete.
          Pero hasta que se ate, <b>no la puede hacer</b>.
        </p>
        {huecos.map((h) => (
          <div key={`${h.skill_id}-${h.capacidad_id}`}
               className="flex flex-wrap items-center gap-2 text-[11px]">
            <code className="font-mono text-[10px] bg-white border border-amber-200 rounded-sm px-1">
              {h.capacidad_id}
            </code>
            <span className="text-zinc-500 text-[10px]">la pide {h.skill_id}</span>
            {h.como_atarla?.length > 0 ? (
              h.como_atarla.map((r) => (
                <Button
                  key={`${r.conexion_id}-${r.herramienta}`}
                  size="sm" variant="outline"
                  data-testid={`atar-${h.capacidad_id}-${r.conexion_id}`}
                  disabled={atando === h.capacidad_id}
                  onClick={() => atar(h, r)}
                  className="h-6 text-[10px] rounded-sm gap-1 border-amber-300"
                  title={`${r.herramienta} · el proyecto o repositorio se elige después`}
                >
                  {atando === h.capacidad_id
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <Link2 className="w-3 h-3" />}
                  atar con {r.conexion_id}
                </Button>
              ))
            ) : (
              <span className="text-[10px] text-zinc-500">
                ninguna conexión dada de alta sabe cumplirlo todavía
              </span>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function FichaDeCapacidad({ capacidad, abierta, onAbrir, tenantId, capsuleId, herramientas, conexiones, onCambio }) {
  const atado = !!capacidad.vinculo;
  const [v, setV] = useState(capacidad.vinculo || { conexion_id: '', herramienta: '', recurso: {}, mapeo: {} });
  const [errores, setErrores] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    setV(capacidad.vinculo || { conexion_id: '', herramienta: '', recurso: {}, mapeo: {} });
    setErrores([]);
  }, [capacidad.vinculo, capacidad.capacidad_id]);

  const elegida = herramientas.find((h) => h.nombre === v.herramienta);

  const atar = async () => {
    setGuardando(true);
    try {
      const { data } = await capacidadesAPI.atar(capacidad.capacidad_id, {
        tenant_id: tenantId, capsule_id: capsuleId, ...v,
      });
      setErrores(data.errores || []);
      if (data.ok) { toast.success('Atada'); onCambio(); }
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo atar');
    } finally {
      setGuardando(false);
    }
  };

  const desatar = async () => {
    await capacidadesAPI.desatar(capacidad.capacidad_id, tenantId, capsuleId);
    toast.success('Desatada');
    onCambio();
  };

  const muerta = capacidad.uso.skills_que_la_piden === 0 && capacidad.uso.capsulas_que_la_atan === 0;
  // **Atada y nadie la pide.** Es configuración que parece viva: el vínculo está bien armado, la
  // pantalla lo muestra en verde, y ningún agente va a llamarla nunca porque ninguna habilidad la
  // declara. Es más difícil de ver que un hueco, porque no se parece a un problema.
  const nadieLaPide = atado && capacidad.uso.skills_que_la_piden === 0;

  return (
    <Card className="rounded-sm border-brand-border" data-testid={`capacidad-${capacidad.capacidad_id}`}>
      <CardHeader className="py-2 px-3 border-b border-brand-border flex-row items-center justify-between gap-2">
        <button className="flex items-center gap-2 text-left min-w-0 flex-1" onClick={onAbrir}>
          {abierta ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
          <div className="min-w-0">
            <CardTitle className="text-xs font-semibold text-[#09090B]">{capacidad.capacidad_id}</CardTitle>
            <p className="text-[10px] text-zinc-500 truncate">{capacidad.descripcion}</p>
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {/* Los dos números son lo que hace que una capacidad muerta se vea muerta: sin ellos
              el catálogo se llena de verbos que nadie sabe si sirven. */}
          <Badge variant="outline" className={`text-[9px] rounded-sm ${muerta ? 'text-zinc-400' : ''}`}>
            {capacidad.uso.skills_que_la_piden} piden · {capacidad.uso.capsulas_que_la_atan} atan
          </Badge>
          {nadieLaPide
            ? <Badge variant="outline" className="text-[9px] rounded-sm text-zinc-500 border-zinc-300 gap-1"
                     title="El vínculo está bien armado, pero ninguna habilidad declara este verbo: ningún agente lo va a pedir nunca.">
                <Unlink className="w-3 h-3" /> atada y nadie la pide
              </Badge>
            : atado
            ? <Badge variant="outline" className="text-[9px] rounded-sm text-green-700 border-green-200 gap-1">
                <CheckCircle2 className="w-3 h-3" /> atada aquí
              </Badge>
            : <Badge variant="outline" className="text-[9px] rounded-sm text-amber-700 border-amber-300 gap-1">
                <AlertCircle className="w-3 h-3" /> sin atar
              </Badge>}
        </div>
      </CardHeader>

      {abierta && (
        <CardContent className="p-3 space-y-2 bg-zinc-50/50">
          {capacidad.parametros?.length > 0 && (
            <p className="text-[10px] text-zinc-500">
              Espera: {capacidad.parametros.map((p) => `${p.nombre}${p.requerido ? '*' : ''}`).join(', ')}
            </p>
          )}

          <div className="flex flex-wrap gap-2 items-end">
            <Campo label="Conexión" ancho="w-40">
              <Select value={v.conexion_id} testid={`vinculo-conexion-${capacidad.capacidad_id}`}
                      onChange={(x) => setV({ ...v, conexion_id: x })}
                      opciones={['', ...(conexiones || []).map((c) => c.conexion_id)]} />
            </Campo>
            <Campo label="Herramienta" ancho="w-64">
              <Select value={v.herramienta} testid={`vinculo-herramienta-${capacidad.capacidad_id}`}
                      onChange={(x) => setV({ ...v, herramienta: x })}
                      opciones={['', ...herramientas.map((h) => h.nombre)]} />
            </Campo>
          </div>

          {/* El recurso: cuál proyecto, cuál repositorio. **Vive acá y nunca en la conexión.** */}
          {elegida?.del_vinculo?.length > 0 && (
            <div className="border border-brand-border rounded-sm bg-white p-2 space-y-1">
              <p className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">
                Sobre qué recurso
              </p>
              <p className="text-[10px] text-zinc-500">
                Cuál proyecto o repositorio. Va acá y no en la conexión: así dos cápsulas usan
                dos proyectos <b>con la misma credencial</b>.
              </p>
              {elegida.del_vinculo.map((k) => (
                <Fila key={k} label={k}>
                  <Input data-testid={`vinculo-recurso-${k}`} value={v.recurso?.[k] || ''}
                         onChange={(e) => setV({ ...v, recurso: { ...(v.recurso || {}), [k]: e.target.value } })}
                         className="rounded-sm text-xs h-7 font-mono" />
                </Fila>
              ))}
            </div>
          )}

          {/* El mapeo: cuando los nombres no coinciden, sin obligar al sistema remoto a
              llamarse como nuestra capacidad. */}
          {elegida && (capacidad.parametros || []).length > 0 && (
            <div className="border border-brand-border rounded-sm bg-white p-2 space-y-1">
              <p className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">
                Cómo se llama cada uno del otro lado
              </p>
              {capacidad.parametros.map((p) => (
                <Fila key={p.nombre} label={`${p.nombre}${p.requerido ? ' *' : ''}`}>
                  <Select value={v.mapeo?.[p.nombre] || p.nombre}
                          testid={`vinculo-mapeo-${p.nombre}`}
                          onChange={(x) => setV({ ...v, mapeo: { ...(v.mapeo || {}), [p.nombre]: x } })}
                          opciones={[p.nombre, ...(elegida.parametros || [])]} />
                </Fila>
              ))}
            </div>
          )}

          {errores.length > 0 && (
            <div data-testid={`errores-vinculo-${capacidad.capacidad_id}`}
                 className="text-[11px] rounded-sm border border-red-200 bg-red-50 text-red-800 p-2 space-y-1">
              {errores.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}

          <div className="flex gap-2">
            <Button size="sm" data-testid={`atar-${capacidad.capacidad_id}`} disabled={guardando}
                    className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-7 px-3 gap-1"
                    onClick={atar}>
              {guardando ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
              {atado ? 'Actualizar' : 'Atar'}
            </Button>
            {atado && (
              <Button size="sm" variant="outline" data-testid={`desatar-${capacidad.capacidad_id}`}
                      className="h-7 text-[10px] rounded-sm gap-1" onClick={desatar}>
                <Unlink className="w-3 h-3" /> Desatar
              </Button>
            )}
          </div>
          {!atado && (
            <p className="text-[10px] text-amber-700">
              Sin atar, la skill que la pide se carga igual y al agente se le dice que esta
              capacidad no está configurada — no desaparece en silencio.
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function Fila({ label, children }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Label className="text-[10px] text-zinc-600 shrink-0 w-28 truncate">{label}</Label>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function Campo({ label, children, ancho = 'w-32' }) {
  return (
    <div className={`${ancho} min-w-0`}>
      <Label className="text-[10px] text-zinc-600 block mb-0.5 truncate">{label}</Label>
      {children}
    </div>
  );
}

function Select({ value, onChange, opciones, testid }) {
  const unicas = [...new Set(opciones)];
  return (
    <select data-testid={testid} value={value || ''} onChange={(e) => onChange(e.target.value)}
            className="w-full rounded-sm text-xs h-7 border border-brand-border bg-white px-1">
      {unicas.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
    </select>
  );
}
